import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, ExternalLink } from 'lucide-react';
import { FlagEN, FlagNL } from '../components/ui/Flag';
import { PreSaleBanner } from '../components/layout/PreSaleBanner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import axios from 'axios';
import { FlowBackground } from '../components/home/FlowBackground';
import { PAGE_BG } from '../components/home/glass';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
    const { t, lang, setLang } = useLanguage();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [emailLang, setEmailLang] = useState(lang);
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [demoLink, setDemoLink] = useState(null);
    const [error, setError] = useState('');

    const handleGoogle = () => {
        // Our own Google OAuth client (not Emergent-managed).
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
        // URLS, THIS BREAKS THE AUTH. The redirect_uri below is intentionally
        // fixed to the production domain because it must exactly match the
        // Authorized redirect URI configured for this client in Google Cloud
        // Console (sign-in therefore only completes from that exact domain).
        const params = new URLSearchParams({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            redirect_uri: 'https://sleepstack.nl/auth/callback',
            response_type: 'code',
            scope: 'openid email profile',
            access_type: 'online',
            prompt: 'select_account',
        });
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        setError('');
        try {
            // Sync UI language with chosen email language
            if (emailLang !== lang) setLang(emailLang);
            const res = await axios.post(`${API}/auth/magic-link`, { email, lang: emailLang });
            setSent(true);
            if (res.data.demo_link) {
                setDemoLink(res.data.demo_link);
            }
        } catch (err) {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <SEO title="Login | Sleepstack" path="/login" />
            <PreSaleBanner />
            <Navbar />
            <main className="relative isolate glass-scope min-h-screen flex items-center justify-center" style={{ paddingTop: 'var(--page-offset, 128px)', background: PAGE_BG }}>
                <FlowBackground variant="neutral" />
                <div className="section-container py-20">
                    <div className="max-w-md mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="card-base p-8 md:p-10"
                        >
                            {!sent ? (
                                <>
                                    <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                                        <Mail size={22} className="text-primary" />
                                    </div>
                                    <h1 className="heading-md text-foreground mb-2">{t('login.title')}</h1>
                                    <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                                        {t('login.subtitle')}
                                    </p>

                                    {/* Google sign-in (our own Google OAuth client) */}
                                    <button
                                        type="button"
                                        onClick={handleGoogle}
                                        data-testid="login-google-btn"
                                        className="w-full h-12 rounded-xl border border-input bg-white text-sm font-semibold text-foreground hover:bg-slate-50 transition-colors flex items-center justify-center gap-3"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                                            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.257h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
                                            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z" />
                                            <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
                                            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
                                        </svg>
                                        {lang === 'nl' ? 'Doorgaan met Google' : 'Continue with Google'}
                                    </button>

                                    {/* Divider */}
                                    <div className="flex items-center gap-3 my-6" aria-hidden="true">
                                        <span className="flex-1 h-px bg-border" />
                                        <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                                            {lang === 'nl' ? 'of' : 'or'}
                                        </span>
                                        <span className="flex-1 h-px bg-border" />
                                    </div>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1.5">
                                                {t('checkout.email')}
                                            </label>
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                placeholder={t('login.email_placeholder')}
                                                required
                                                data-testid="login-email-input"
                                                className="w-full h-12 px-4 border border-input rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                                            />
                                        </div>

                                        {/* Email language preference */}
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1.5">
                                                {lang === 'nl' ? 'E-mailtaal' : 'Email language'}
                                            </label>
                                            <div className="grid grid-cols-2 gap-2" data-testid="login-lang-toggle">
                                                {[
                                                    { v: 'en', label: 'English', Flag: FlagEN },
                                                    { v: 'nl', label: 'Nederlands', Flag: FlagNL },
                                                ].map(o => (
                                                    <button
                                                        key={o.v}
                                                        type="button"
                                                        onClick={() => setEmailLang(o.v)}
                                                        data-testid={`login-lang-${o.v}`}
                                                        className={`h-11 rounded-xl border text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                                                            emailLang === o.v
                                                                ? 'border-primary bg-primary/5 text-primary'
                                                                : 'border-border text-muted-foreground hover:border-primary/50'
                                                        }`}
                                                    >
                                                        <o.Flag size={14} /> {o.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                                {lang === 'nl'
                                                    ? 'We sturen al je e-mails (login, bevestiging, verzending) in deze taal. Je kunt dit later wijzigen in je account.'
                                                    : "We'll send all your emails (login, confirmation, shipping updates) in this language. You can change it later from your account."}
                                            </p>
                                        </div>

                                        {error && (
                                            <p className="text-red-500 text-sm">{error}</p>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={loading || !email}
                                            data-testid="login-submit"
                                            className="btn-secondary w-full disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {loading ? t('login.sending') : t('login.submit')}
                                        </button>
                                    </form>
                                </>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.97 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                                        <span className="text-2xl">✓</span>
                                    </div>
                                    <h2 className="heading-sm text-foreground mb-2">{t('login.sent_title')}</h2>
                                    <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                                        {t('login.sent_desc')} <strong>{email}</strong>.
                                    </p>

                                    {demoLink && (
                                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                                            <p className="text-xs text-amber-700 font-medium mb-2">{t('login.demo_note')}</p>
                                            <a
                                                href={demoLink}
                                                data-testid="demo-login-link"
                                                className="flex items-center gap-2 text-sm font-bold text-amber-700 hover:underline"
                                            >
                                                <ExternalLink size={14} />
                                                {t('login.demo_btn')}
                                            </a>
                                        </div>
                                    )}

                                    <Link
                                        to="/"
                                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                                    >
                                        <ArrowLeft size={14} />
                                        {t('login.back_home')}
                                    </Link>
                                </motion.div>
                            )}
                        </motion.div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
